import type { WorkAreaLocale } from './index.js';

// URL translations are separate from the stable keys used for drafts, files and submitted applications.
const slugs = {
  pflege: { de: 'pflege', en: 'care', tr: 'bakim' },
  gastro: { de: 'gastronomie-service', en: 'hospitality-service', tr: 'konaklama-hizmetleri' },
  handwerk: { de: 'handwerk', en: 'skilled-trades', tr: 'zanaat' },
  einzelhandel: { de: 'einzelhandel', en: 'retail', tr: 'perakende' },
  pflegefachkraft: { de: 'pflegefachkraft', en: 'nurse', tr: 'hemsire' },
  'op-pflege-ota': { de: 'op-pflege-ota', en: 'surgical-nurse-ota', tr: 'ameliyathane-hemsiresi-ota' },
  'radiologie-mtra': { de: 'radiologie-mtra', en: 'radiology-technologist-mtra', tr: 'radyoloji-teknikeri-mtra' },
  'medizinische-fachangestellte': { de: 'medizinische-fachangestellte', en: 'medical-assistant', tr: 'tibbi-asistan' },
  physiotherapie: { de: 'physiotherapie', en: 'physiotherapy', tr: 'fizyoterapi' },
  'labor-gesundheit': { de: 'labor-gesundheit', en: 'laboratory-healthcare', tr: 'laboratuvar-saglik' },
  'weitere-gesundheitsberufe': { de: 'weitere-gesundheitsberufe', en: 'other-healthcare-professions', tr: 'diger-saglik-meslekleri' },
  elektriker: { de: 'elektriker', en: 'electrician', tr: 'elektrikci' },
  bauwesen: { de: 'bauwesen', en: 'construction', tr: 'insaat' },
  schweisser: { de: 'schweisser', en: 'welder', tr: 'kaynakci' },
  cnc: { de: 'cnc', en: 'cnc', tr: 'cnc' },
  shk: { de: 'shk', en: 'plumbing-heating-air-conditioning', tr: 'sihhi-tesisat-isitma-klima' },
  'lkw-fahrer': { de: 'lkw-fahrer', en: 'truck-driver', tr: 'kamyon-soforu' },
  gastronomie: { de: 'gastronomie', en: 'gastronomy', tr: 'gastronomi' },
  baecker: { de: 'baecker', en: 'baker', tr: 'firinci' },
  metzger: { de: 'metzger', en: 'butcher', tr: 'kasap' },
  frischetheke: { de: 'frischetheke', en: 'fresh-food-counter', tr: 'taze-urun-tezgahi' },
} as const satisfies Record<string, Record<WorkAreaLocale, string>>;

export type ApplicationFormKey = keyof typeof slugs;
export const applicationFormKeys = Object.keys(slugs) as ApplicationFormKey[];
const roots: Record<WorkAreaLocale, `/${string}`> = {
  de: '/bewerbungsformular', en: '/application-form', tr: '/basvuru-formu',
};
export const applicationPath = (key: ApplicationFormKey | undefined, locale: WorkAreaLocale): `/${string}` =>
  `${roots[locale]}${key ? `/${slugs[key][locale]}` : ''}`;

export interface ApplicationRoute {
  path: string;
  key?: ApplicationFormKey;
  locale?: WorkAreaLocale;
}

const routes = new Map<string, ApplicationRoute>();
for (const locale of ['de', 'en', 'tr'] as const) {
  const overview = applicationPath(undefined, locale);
  // The original overview URL is shared by old links in every language, so it keeps the cookie's locale.
  routes.set(overview, { path: overview, ...(locale === 'tr' ? {} : { locale }) });
  for (const key of applicationFormKeys) {
    const path = applicationPath(key, locale);
    routes.set(path, { path, key, locale });
  }
}
for (const key of applicationFormKeys) {
  const path = `/basvuru-formu/${key}`;
  if (!routes.has(path)) routes.set(path, { path, key });
}
export const applicationRoutes: readonly ApplicationRoute[] = [...routes.values()];
export const resolveApplicationPath = (path: string): ApplicationRoute | undefined =>
  routes.get(path.replace(/\/+$/u, '') || '/');
